import React, { useRef, useState, useEffect } from 'react'
import './ScrollableList.scss'
import { NextButton, PrevButton } from '../EmblaCarouselArrowButtons'
import { DotButton } from '../EmblaCarouselDotButton'
import { useMediaQuery } from 'hooks/useMediaQuery'

export const ScrollableList = ({
  children,
  showButtons = false,
  showArrowButtons = false,
}) => {
  const isMobile = useMediaQuery('(max-width: 768px)')
  const scrollContainerRef = useRef(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(true)
  const [activeDotIndex, setActiveDotIndex] = useState(0)
  const [totalDots, setTotalDots] = useState(1)

  // Slide configuration
  const SLIDES_PER_CLICK = 3 // Number of slides to scroll per click
  const SLIDE_WIDTH = 140 // Width of each slide in pixels
  const MOBILE_SLIDE_WIDTH = 80 // Width of each slide in pixels
  const SLIDE_GAP = 30 // Gap between slides in pixels
  const MOBILE_SLIDE_GAP = 8 // Gap between slides on mobile

  // Dynamic scroll amount based on screen size
  const currentSlideWidth = isMobile ? MOBILE_SLIDE_WIDTH : SLIDE_WIDTH
  const currentSlideGap = isMobile ? MOBILE_SLIDE_GAP : SLIDE_GAP
  const SCROLL_AMOUNT = (currentSlideWidth + currentSlideGap) * SLIDES_PER_CLICK

  // Calculate which page (dot) is currently active
  const calculateActiveDot = (scrollLeft, containerWidth, totalWidth) => {
    if (totalWidth <= containerWidth) return 0 // Everything fits in one view

    // Calculate total scroll distance and how many "pages" we need
    const scrollableDistance = totalWidth - containerWidth
    const scrollRatio = scrollLeft / scrollableDistance

    // Calculate which dot should be active
    return Math.min(totalDots - 1, Math.round(scrollRatio * (totalDots - 1)))
  }

  // Calculate total number of dots needed
  const calculateTotalDots = (container, childCount) => {
    if (!container) return 1

    const containerWidth = container.clientWidth
    const slideWidth = isMobile ? MOBILE_SLIDE_WIDTH : SLIDE_WIDTH
    const slideGap = isMobile ? MOBILE_SLIDE_GAP : SLIDE_GAP
    const totalWidth = childCount * (slideWidth + slideGap)

    // If everything fits on screen, we need just 1 dot
    if (totalWidth <= containerWidth) return 1

    // Calculate how many complete pages we have
    // Each page shows SLIDES_PER_CLICK slides
    const slidesPerView = Math.floor(containerWidth / (slideWidth + slideGap))
    const numFullPages =
      Math.floor((childCount - slidesPerView) / SLIDES_PER_CLICK) + 1

    // If there are remaining slides that don't fill a complete page, add one more dot
    const hasRemainderSlides =
      (childCount - slidesPerView) % SLIDES_PER_CLICK !== 0

    return numFullPages + (hasRemainderSlides ? 1 : 0)
  }

  // Check scroll possibilities and update active dot
  const checkScrollability = () => {
    const container = scrollContainerRef.current
    if (!container) return

    // Can scroll left if scrolled away from start
    setCanScrollLeft(container.scrollLeft > 0)

    // Can scroll right if there's more content to the right
    const hasMoreContent =
      container.scrollWidth > container.clientWidth + container.scrollLeft
    setCanScrollRight(hasMoreContent)

    // Calculate which dot should be active based on scroll position
    const newActiveDot = calculateActiveDot(
      container.scrollLeft,
      container.clientWidth,
      container.scrollWidth,
    )
    setActiveDotIndex(newActiveDot)
  }

  // Calculate number of dots based on content and recalculate when screen size changes
  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    // Wait for layout to be complete
    setTimeout(() => {
      const childCount = React.Children.count(children)
      const dots = calculateTotalDots(container, childCount)
      setTotalDots(Math.max(1, dots))
    }, 100)
  }, [children, isMobile]) // Re-run when isMobile changes

  // Setup event listeners
  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    // Initial check
    checkScrollability()

    // Check on scroll
    const handleScroll = () => checkScrollability()
    container.addEventListener('scroll', handleScroll)

    // Check on resize
    const handleResize = () => {
      // Recalculate dots on resize
      const childCount = React.Children.count(children)
      const dots = calculateTotalDots(container, childCount)
      setTotalDots(Math.max(1, dots))

      checkScrollability()
    }
    window.addEventListener('resize', handleResize)

    return () => {
      container.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleResize)
    }
  }, [children, totalDots, isMobile]) // Re-run when isMobile changes

  // Scroll handlers
  const scrollLeft = () => {
    const container = scrollContainerRef.current
    if (!container) return

    container.scrollBy({
      left: -SCROLL_AMOUNT,
      behavior: 'smooth',
    })
  }

  const scrollRight = () => {
    const container = scrollContainerRef.current
    if (!container) return

    container.scrollBy({
      left: SCROLL_AMOUNT,
      behavior: 'smooth',
    })
  }

  // Special scroll to end - ensures ALL slides are visible at the end
  const scrollToEnd = () => {
    const container = scrollContainerRef.current
    if (!container) return

    // Scroll to very end
    container.scrollTo({
      left: container.scrollWidth - container.clientWidth,
      behavior: 'smooth',
    })
  }

  // Scroll to a specific dot/section
  const scrollToDot = dotIndex => {
    const container = scrollContainerRef.current
    if (!container) return

    // Calculate scroll position based on dot index
    const childCount = React.Children.count(children)
    const containerWidth = container.clientWidth
    const slideWidth = isMobile ? MOBILE_SLIDE_WIDTH : SLIDE_WIDTH
    const slideGap = isMobile ? MOBILE_SLIDE_GAP : SLIDE_GAP
    const totalWidth = childCount * (slideWidth + slideGap)

    if (dotIndex === totalDots - 1) {
      // If it's the last dot, scroll all the way to the end
      scrollToEnd()
    } else {
      // Otherwise, scroll proportionally to the dot index
      const scrollableDistance = totalWidth - containerWidth
      const targetPosition = (dotIndex / (totalDots - 1)) * scrollableDistance

      container.scrollTo({
        left: targetPosition,
        behavior: 'smooth',
      })
    }
  }

  return (
    <section className="scrollable-list">
      <div className="scrollable-list__container" ref={scrollContainerRef}>
        {React.Children.map(children, (child, index) => (
          <div className="scrollable-list__slide" key={index}>
            {child}
          </div>
        ))}
      </div>

      <div className="scrollable-list__controls">
        {showArrowButtons && (
          <div className="scrollable-list__buttons">
            <PrevButton onClick={scrollLeft} disabled={!canScrollLeft} />

            {showButtons && totalDots > 1 && (
              <div className="scrollable-list__dots">
                {Array.from({ length: totalDots }).map((_, index) => (
                  <DotButton
                    key={index}
                    onClick={() => scrollToDot(index)}
                    className={`scrollable-list-embla__dot${
                      index === activeDotIndex
                        ? ' scrollable-list-embla__dot--selected'
                        : ''
                    }`}
                  />
                ))}
              </div>
            )}

            <NextButton
              onClick={
                canScrollRight
                  ? // If we can scroll right but not much left, go to very end
                    scrollContainerRef.current?.scrollWidth -
                      scrollContainerRef.current?.clientWidth -
                      scrollContainerRef.current?.scrollLeft <
                    SCROLL_AMOUNT * 0.5
                    ? scrollToEnd
                    : scrollRight
                  : null
              }
              disabled={!canScrollRight}
            />
          </div>
        )}
      </div>
    </section>
  )
}
